import { createContext, useContext } from 'react';
import { emptyDesign, sanitiseDesign, type Design } from '../design/schema';

export type TabId = 'route' | 'style' | 'text' | 'labels' | 'map' | 'size';

export interface EditorState {
  design: Design;
  past: Design[];
  future: Design[];
  /** Consecutive edits with the same key (typing in one field, dragging one slider) are one undo step. */
  lastKey: string | null;
  lastAt: number;
  tab: TabId;
  selectedLabel: string | null;
  /** Waiting for a tap on the map to drop a new label. */
  placing: boolean;
  /** 'ordered' designs are read-only on the server; edits fork a copy. */
  locked: boolean;
}

export type Action =
  | { type: 'edit'; recipe: (d: Design) => Design; key?: string; at: number }
  | { type: 'load'; design: Design; locked?: boolean; tab?: TabId }
  | { type: 'assignId'; id: string | null; locked?: boolean }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'tab'; tab: TabId }
  | { type: 'selectLabel'; id: string | null }
  | { type: 'placing'; on: boolean };

const HISTORY = 80;
const COALESCE_MS = 1200;

export const initialState = (design: Design = emptyDesign()): EditorState => ({
  design,
  past: [],
  future: [],
  lastKey: null,
  lastAt: 0,
  tab: design.routes.length ? 'style' : 'route',
  selectedLabel: null,
  placing: false,
  locked: false,
});

export function reducer(state: EditorState, action: Action): EditorState {
  switch (action.type) {
    case 'edit': {
      const design = action.recipe(state.design);
      if (design === state.design) return state;
      const coalesce = action.key != null && action.key === state.lastKey && action.at - state.lastAt < COALESCE_MS;
      return {
        ...state,
        design,
        past: coalesce ? state.past : [...state.past.slice(-HISTORY + 1), state.design],
        future: [],
        lastKey: action.key ?? null,
        lastAt: action.at,
      };
    }
    case 'load':
      return { ...initialState(action.design), locked: action.locked ?? false, tab: action.tab ?? (action.design.routes.length ? 'style' : 'route') };
    case 'assignId':
      // Bookkeeping from the server, not a user edit: no history entry.
      return { ...state, design: { ...state.design, id: action.id }, locked: action.locked ?? state.locked };
    case 'undo': {
      const prev = state.past[state.past.length - 1];
      if (!prev) return state;
      return {
        ...state,
        design: { ...prev, id: state.design.id },
        past: state.past.slice(0, -1),
        future: [state.design, ...state.future],
        lastKey: null,
        selectedLabel: null,
        placing: false,
      };
    }
    case 'redo': {
      const next = state.future[0];
      if (!next) return state;
      return {
        ...state,
        design: { ...next, id: state.design.id },
        past: [...state.past, state.design],
        future: state.future.slice(1),
        lastKey: null,
        selectedLabel: null,
        placing: false,
      };
    }
    case 'tab':
      return { ...state, tab: action.tab, placing: action.tab === 'labels' ? state.placing : false };
    case 'selectLabel':
      return { ...state, selectedLabel: action.id, tab: action.id ? 'labels' : state.tab, placing: false };
    case 'placing':
      return { ...state, placing: action.on, selectedLabel: action.on ? null : state.selectedLabel };
  }
}

export interface Editor {
  state: EditorState;
  dispatch: (a: Action) => void;
  /** Apply a change to the design as an undoable step. */
  edit: (recipe: (d: Design) => Design, key?: string) => void;
}

export const EditorContext = createContext<Editor | null>(null);

export function useEditor(): Editor {
  const ctx = useContext(EditorContext);
  if (!ctx) throw new Error('useEditor outside EditorContext');
  return ctx;
}

// --- local draft ------------------------------------------------------------

const DRAFT_KEY = 'tm-designer:draft:v2';

export function saveDraft(design: Design) {
  try {
    if (design.routes.length) localStorage.setItem(DRAFT_KEY, JSON.stringify({ savedAt: Date.now(), design }));
    else localStorage.removeItem(DRAFT_KEY);
  } catch {
    // Storage full or blocked (private mode): the draft is a convenience, not a requirement.
  }
}

export function loadDraft(): { design: Design; savedAt: number } | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const design = sanitiseDesign(parsed.design);
    return design.routes.length ? { design, savedAt: Number(parsed.savedAt) || 0 } : null;
  } catch {
    return null;
  }
}

export function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
}
